import {
  ArrowRightIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@mui/joy';

import FewShotSearchEmptyState from '@/assets/fewShotSearch.svg?react';
import FewShotSearchEmptyStateDark from '@/assets/fewShotSearchDark.svg?react';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Tooltip } from '@/design-system';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useDataset, useSyncDatasetForFewShotMutation } from '@/hooks/useSwr';
import { cn } from '@/utils/tailwind';

export const FewShotSelector = ({
  datasetId,
  isLoading,
  setEditDatasetOpen,
}: {
  datasetId: string;
  isLoading: boolean;
  setEditDatasetOpen?: () => void;
}) => {
  const { isDarkMode } = useColorScheme();
  const {
    trigger: syncDatasetForFewShot,
    isMutating: isSyncingDatasetForFewShot,
    error: syncDatasetForFewShotError,
  } = useSyncDatasetForFewShotMutation(datasetId);

  const dataset = useDataset(datasetId);

  // const [selectedVersion, setSelectedVersion] =
  //   useState<GroupedDatasetVersionSchemaWithOptionalAsOf | null>(null);
  // const [searchVersion, setSearchVersion] = useState<string>('');
  // const [searchVersionDebounced] = useDebounce(searchVersion, 250);
  // const {
  //   data: versionPages,
  //   size,
  //   setSize,
  //   isLoading: versionPagesLoading,
  // } = useDatasetVersionsInfinite(VERSIONS_PAGE_SIZE, datasetId ?? null, {
  //   search: searchVersionDebounced,
  // });

  // const versionsFlat = useMemo(
  //   () => versionPages?.flat() ?? [],
  //   [versionPages]
  // );

  // const selectedTag = selectedVersion?.tags?.[0] ?? selectedVersion?.as_of;

  const noInputSchema = dataset.data?.inputs_schema_definition == null;

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3">
      {noInputSchema && (
        <div className="relative mt-[100px]  inline-flex w-[400px] items-start justify-center gap-4 self-center rounded-xl border border-tertiary p-4 shadow">
          <div className="relative h-5 w-5 rounded-[20px]">
            <div className="absolute left-[-4px] top-[-4px] h-7 w-7 rounded-3xl border-2 border-ls-orange-500 opacity-30" />
            <div className="absolute left-[-9px] top-[-9px] h-[38px] w-[38px] rounded-3xl border-2 border-ls-orange-500 opacity-10" />
            <div className="absolute left-0 top-0 h-5 w-5" />
            <ExclamationCircleIcon className="absolute left-0 top-0 h-5 w-5 stroke-ls-orange-500" />
          </div>
          <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-3">
            <div className="flex flex-col items-start justify-start gap-1 self-stretch">
              <div className="shrink grow basis-0 pr-8 text-sm">
                You must set an input schema on your dataset before you can
                index it for few-shot examples.
              </div>
            </div>
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={setEditDatasetOpen}
              endDecorator={<ArrowRightIcon className="h-4 w-4" />}
            >
              Edit Dataset
            </Button>
          </div>
        </div>
      )}
      <div
        className={cn(
          'relative w-[400px] overflow-hidden rounded-lg border border-secondary p-4 pt-[100px]',
          !noInputSchema && 'mt-[100px] '
        )}
      >
        <div className="absolute -left-[200px] -top-[200px] -z-10 flex items-center justify-center">
          {isDarkMode ? (
            <FewShotSearchEmptyStateDark />
          ) : (
            <FewShotSearchEmptyState className="stroke-black" />
          )}
        </div>
        <div className="flex flex-col gap-4 bg-background p-2">
          <div>
            <div className="text-left font-semibold">
              Index datasets for few-shot examples
            </div>
            <div className="text-grey-500 text-sm dark:text-gray-400">
              Search high-quality few-shot example without the need to manage a
              vectorstore or search database.
            </div>
          </div>
          <div className="text-grey-500 flex flex-col gap-2 text-sm dark:text-gray-400">
            We will sync the latest version of the dataset.
          </div>
          {/* <div className="flex flex-col gap-2">
            <div className="text-sm">Dataset version</div>
            <DatasetVersionDropdownMenu
              className="p-1.5"
              selectedVersion={selectedVersion}
              datasetId={datasetId}
              setSelectedVersion={setSelectedVersion}
              versions={versionsFlat}
              size={size}
              setSize={setSize}
              isLoadingVersions={versionPagesLoading}
              totalVersions={Number(
                versionPages?.[versionPages.length - 1]?.headers?.[
                  'x-pagination-total'
                ] ?? 0
              )}
              setSearch={setSearchVersion}
              search={searchVersion}
              isLoadingCurrentVersion={false}
              versionPageSize={VERSIONS_PAGE_SIZE}
              placeholder="Select a version"
            />
          </div> */}
          {syncDatasetForFewShotError && (
            <ExpandableErrorAlert error={syncDatasetForFewShotError} />
          )}
          <div className="flex justify-end ">
            <Tooltip
              title={
                noInputSchema
                  ? 'You must enable input schema validation on your dataset in order to index it.'
                  : undefined
              }
            >
              <span>
                <Button
                  loading={isSyncingDatasetForFewShot || isLoading}
                  // disabled={!selectedTag}
                  onClick={() => {
                    // if (!selectedTag) return;
                    syncDatasetForFewShot({
                      json: {
                        tag: 'latest',
                      },
                    });
                  }}
                  disabled={noInputSchema}
                >
                  Start sync
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};
